/**
 * Global state tracker to prevent excessive Redux state updates
 * This helps prevent refresh loops caused by frequent state changes
 */

class StateUpdateTracker {
  private lastUpdates: Map<string, number> = new Map()
  private updateCounts: Map<string, number> = new Map()
  private readonly maxUpdatesPerMinute = 10
  private readonly cooldownPeriod = 60000 // 1 minute

  /**
   * Check if a state update should be allowed
   * @param key - Unique identifier for the state update
   * @param minInterval - Minimum interval between updates in milliseconds
   * @returns true if update is allowed, false if blocked
   */
  shouldAllowUpdate(key: string, minInterval: number = 5000): boolean {
    const now = Date.now()
    const lastUpdate = this.lastUpdates.get(key) || 0
    const updateCount = this.updateCounts.get(key) || 0

    // Check if enough time has passed since last update
    if (now - lastUpdate < minInterval) {
      console.log(`⏳ State update blocked for ${key}: too soon (${now - lastUpdate}ms ago)`)
      return false
    }

    // Check if we've exceeded the maximum updates per minute
    if (updateCount >= this.maxUpdatesPerMinute) {
      console.log(`🛑 State update blocked for ${key}: exceeded max updates per minute (${updateCount})`)
      return false
    }

    // Allow the update
    this.lastUpdates.set(key, now)
    this.updateCounts.set(key, updateCount + 1)

    // Reset counter after cooldown period
    setTimeout(() => {
      this.updateCounts.set(key, 0)
    }, this.cooldownPeriod)

    return true
  }

  /**
   * Force reset all counters (useful for testing or manual reset)
   */
  reset(): void {
    this.lastUpdates.clear()
    this.updateCounts.clear()
  }

  /**
   * Get current state for debugging
   */
  getState(): { lastUpdates: Map<string, number>; updateCounts: Map<string, number> } {
    return {
      lastUpdates: new Map(this.lastUpdates),
      updateCounts: new Map(this.updateCounts)
    }
  }
}

// Export singleton instance
export const stateUpdateTracker = new StateUpdateTracker()

export default stateUpdateTracker
